import { css, cva } from '@styled-system/css';
import { forwardRef, type ReactNode, useId } from 'react';

const wrapper = css({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '4',
  cursor: 'pointer',
  userSelect: 'none',
  fontFamily: 'sans',
  fontSize: 'bodySm',
  color: 'text.primary',
  _disabled: { cursor: 'not-allowed', opacity: 0.5 },
});

const hiddenInput = css({
  position: 'absolute',
  opacity: 0,
  pointerEvents: 'none',
  width: 0,
  height: 0,
});

const box = cva({
  base: {
    width: '16px',
    height: '16px',
    borderRadius: 'sm',
    borderWidth: '1px',
    borderStyle: 'solid',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    bg: 'surface.default',
    borderColor: 'border.strong',
    transition: 'background 120ms ease, border-color 120ms ease',
    flexShrink: 0,
  },
  variants: {
    on: {
      true:  { bg: 'brand.primary', borderColor: 'brand.primary', color: 'white' },
      false: {},
    },
  },
});

interface CheckboxProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: ReactNode;
  indeterminate?: boolean;
  disabled?: boolean;
  className?: string;
  id?: string;
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ checked, onChange, label, indeterminate = false, disabled, className, id }, ref) => {
    const reactId = useId();
    const inputId = id ?? reactId;
    const filled = checked || indeterminate;
    return (
      <label className={`${wrapper} ${className ?? ''}`} aria-disabled={disabled} htmlFor={inputId}>
        <input
          ref={ref}
          id={inputId}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked)}
          className={hiddenInput}
        />
        <span className={box({ on: filled })}>
          {checked && (
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden>
              <path d="M1.5 5.2L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
          {indeterminate && !checked && (
            <span style={{ width: 8, height: 2, background: 'currentColor', borderRadius: 1 }} />
          )}
        </span>
        {label && <span>{label}</span>}
      </label>
    );
  },
);

Checkbox.displayName = 'Checkbox';
